import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { DateInputUtils } from '@shared/utils';
import { BaseSelect } from '../select';

const days = Array(31)
  .fill(undefined)
  .map((_, i) => i + 1)
  .map((v) => v.toString().padStart(2, '0'));

interface DateInputProps {
  className?: string;
  value: string;
  formStyle?: boolean;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  caption: string;
}

interface DateParts {
  day: string;
  month: string;
  year: string;
}

export const DateInput = (props: DateInputProps) => {
  const { t } = useTranslation();
  const months = useMemo(
    () => [
      t('months.january'),
      t('months.february'),
      t('months.march'),
      t('months.april'),
      t('months.may'),
      t('months.june'),
      t('months.july'),
      t('months.august'),
      t('months.september'),
      t('months.october'),
      t('months.november'),
      t('months.december'),
    ],
    [t],
  );
  const [parts, setParts] = useState<DateParts>(() => DateInputUtils.valueToParts(props.value, months));

  useEffect(() => {
    setParts(DateInputUtils.valueToParts(props.value, months));
  }, [props.value]);

  const handlePartChange = (part: keyof DateParts, newValue: string | number) => {
    setParts((prev) => {
      const newParts = { ...prev, [part]: newValue };
      const formattedValue = DateInputUtils.partsToValue(newParts, months);
      if (formattedValue) props.onChange(formattedValue);
      return newParts;
    });
  };

  const years = useMemo(() => DateInputUtils.generateYears('adult'), []);
  const daysOptions = useMemo(() => days.map((day) => ({ value: day, label: day })), [days]);
  const monthsOptions = useMemo(() => months.map((month) => ({ value: month, label: month })), [months]);
  const yearsOptions = useMemo(() => years.map((year) => ({ value: year.toString(), label: year.toString() })), [years]);
  return (
    <div className={clsx('relative z-1000', props.className)}>
      <div className='mb-[6px]'>
        {props.required && <span className='pr-2 text-xl'>*</span>}
        <label className='text-lg tracking-[-0.1px] leading-[25px] text-[#4c4c52] mb-2'>{props.caption}</label>
      </div>

      <div className='flex'>
        <BaseSelect
          className='!w-[22%] [&:not(:last-child)]:border-r-0 !mb-0'
          selectClassName={props.error ? 'border-red-600 shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)] border-r-0' : ''}
          values={daysOptions}
          placeholder={t('dateInput.placeholders.day')}
          value={parts.day}
          onChange={(value) => handlePartChange('day', value)}
        />
        <BaseSelect
          className='!w-[53%] [&:not(:last-child)]:border-r-0 !mb-0'
          selectClassName={props.error ? 'border-red-600 shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)] border-r-0' : ''}
          values={monthsOptions}
          placeholder={t('dateInput.placeholders.month')}
          value={parts.month}
          onChange={(value) => handlePartChange('month', value)}
        />
        <BaseSelect
          className='!w-[22%] [&:not(:last-child)]:border-r-0 !mb-0'
          selectClassName={props.error ? 'border-red-600 shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)] border-r-0' : ''}
          values={yearsOptions}
          placeholder={t('dateInput.placeholders.year')}
          value={parts.year}
          onChange={(value) => handlePartChange('year', value)}
        />
      </div>
      {props.error && (
        <div
          className={clsx(
            'text-sm left-0 mt-0  top-full text-[#d44112] tracking-[-0.1px] text-left whitespace-pre-line',
            props.formStyle && 'absolute top-0',
          )}
        >
          {props.error}
        </div>
      )}
      <input type='hidden' value={props.value || 'text-red-500 text-[16px] tracking-[-0.1px] mt-2'} />
    </div>
  );
};
